import { useEffect, useState } from 'react';
import { CloseIcon, DownloadIcon, LoadingSpinner } from '@components/icons';
import { publicPath } from '@utils/public-path';

interface AppBuild {
  id: string;
  label: string;
  detail: string;
  /** Download URL; null while the build isn't published yet. */
  url: string | null;
}

interface DownloadsManifest {
  app: { version: string; builds: AppBuild[] };
}

interface GetAppModalProps {
  onClose: () => void;
}

/**
 * "Get the App" — SKJV Desktop downloads for every platform, driven by the
 * downloads.json manifest (snug pattern): publishing a build is a manifest
 * edit, never a code change.
 */
export function GetAppModal({ onClose }: GetAppModalProps) {
  const [manifest, setManifest] = useState<DownloadsManifest | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetch(publicPath('downloads/downloads.json'))
      .then((res) => res.json())
      .then(setManifest)
      .catch(() => setFailed(true));
  }, []);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-stone-800"
        data-testid="get-app-modal"
      >
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-200">
            Get the App
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-stone-400 transition-colors hover:text-stone-600 dark:hover:text-stone-300"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="space-y-4">
          <p className="text-sm text-stone-600 dark:text-stone-400">
            SKJV Desktop is the full study platform — your studies, groups, and
            AI features, living in files you own, working offline.
          </p>

          {failed && (
            <p className="text-sm text-red-600 dark:text-red-400">
              Could not load the downloads list. Please try again later.
            </p>
          )}
          {!manifest && !failed && (
            <div className="flex justify-center py-4">
              <LoadingSpinner className="h-5 w-5 animate-spin text-stone-400" />
            </div>
          )}

          {manifest?.app.builds.map((build) =>
            build.url ? (
              <a
                key={build.id}
                href={build.url}
                download
                data-testid={`get-app-${build.id}`}
                className="flex items-center justify-between rounded-lg border border-stone-200 p-3 transition-colors hover:bg-stone-50 dark:border-stone-700 dark:hover:bg-stone-700/50"
              >
                <span>
                  <span className="block font-medium text-stone-800 dark:text-stone-200">
                    {build.label}
                  </span>
                  <span className="block text-xs text-stone-500 dark:text-stone-400">
                    {build.detail}
                  </span>
                </span>
                <DownloadIcon className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
              </a>
            ) : (
              <div
                key={build.id}
                data-testid={`get-app-${build.id}`}
                className="flex items-center justify-between rounded-lg border border-stone-200 p-3 opacity-70 dark:border-stone-700"
              >
                <span>
                  <span className="block font-medium text-stone-800 dark:text-stone-200">
                    {build.label}
                  </span>
                  <span className="block text-xs text-stone-500 dark:text-stone-400">
                    {build.detail}
                  </span>
                </span>
                <span className="rounded-full bg-stone-100 px-2 py-1 text-xs text-stone-500 dark:bg-stone-700 dark:text-stone-400">
                  Coming soon
                </span>
              </div>
            ),
          )}

          {manifest && (
            <p className="text-center text-xs text-stone-400 dark:text-stone-500">
              Version {manifest.app.version}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
